import React, { useState, useEffect, useContext } from 'react';
import ReactTable from 'react-table';
import PropTypes from 'prop-types';
import { Badge } from 'react-bootstrap';
import { Validator } from 'blaze-validator';
import FileNotFound from '../FileNotFound';
import LoyaltyContext from './LoyaltyContext';
import ModalContext from '../modal/ModalContext';

const RedemptionList = ({ customerId, query = [], onQueryClick = () => {}, params = {} }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [showPagination, setShowPagination] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(-1);
  const [error, setError] = useState('');

  const { setTitle } = useContext(ModalContext);

  const { fetchRedemptions } = useContext(LoyaltyContext);

  const columns = [
    {
      Header: 'Redemption',
      columns: [
        { Header: 'Unique ID', accessor: 'UniqueId' },
        {
          Header: 'Date',
          accessor: 'RedemptionDate',
          Cell: r => <span>{new Date(r.original.RedemptionDate).toLocaleString()}</span>,
        },
        { Header: 'Code', accessor: 'RedeemApprovalCode' },
        { Header: 'By', accessor: 'RedeemedBy' },
      ],
    },
    {
      Header: 'Invoice Details',
      columns: [
        { Header: 'Invoice', accessor: 'InvoiceNumber' },
        {
          Header: 'Amount',
          accessor: 'Amount',
          className: 'amount',
          Cell: row => {
            return (
              <div className="amount">
                {Validator.sanitize('number', row.original.Amount, { precision: 2 })} {row.original.Currency}
              </div>
            );
          },
        },
        { Header: 'Offer ID', accessor: 'OfferUniqueId' },
        { Header: 'Coupon', accessor: 'CouponCode' },
      ],
    },
    {
      Header: 'Void',
      columns: [
        {
          Header: 'Status',
          accessor: 'Void',
          className: 'text-center',
          Cell: row => {
            if (row.original.Void === true) {
              return <Badge className="void">Void</Badge>;
            }

            return <Badge className="redeemed">Redeemed</Badge>;
          },
        },
        {
          Header: 'Date',
          accessor: 'VoidDate',
          Cell: r => {
            if (!r.original.VoidDate) {
              return '';
            }

            return <span>{new Date(r.original.VoidDate).toLocaleString()}</span>;
          },
        },
        { Header: 'By', accessor: 'VoidBy' },
        { Header: 'Dealer', accessor: 'VoidDealerCode' },
      ],
    },
  ];

  const getRedemptions = ({ page, pageSize, sorted }) => {
    const search = {
      pageSize,
      page: page + 1,
      ...params,
    };

    // May not even be sortable
    if (sorted && sorted[0]) {
      const k = sorted[0].id;
      search.sortBy = `${k.charAt(0).toLowerCase()}${k.slice(1)}`;
      search.sortDir = sorted[0].desc ? 'DESC' : 'ASC';
    }

    setLoading(true);

    if (setTitle) {
      setTitle(`Redemptions for Customer ${customerId}`);
    }

    fetchRedemptions(customerId, search)
      .then(response => {
        const { TotalResults, PageSize, CurrentPage, TotalPage, Results } = response.data;

        if (TotalPage > 1) {
          setShowPagination(true);
        }

        setList(Results);
        setLoading(false);
        setPage(CurrentPage - 1);
        setPageSize(PageSize);
        setTotal(TotalResults);
        setTotalPages(TotalPage);
      })
      .catch(e => {
        setLoading(false);

        const { response } = e;

        if (response.status === 404) {
          setTotal(0);
          setTotalPages(0);
        } else {
          setError('Error getting redemptions');
        }
      });
  };

  const renderTable = () => {
    return (
      <ReactTable
        className="-striped -highlight"
        columns={columns}
        data={list}
        defaultPageSize={pageSize}
        loading={loading}
        manual
        onFetchData={state => getRedemptions({ ...state })}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[2, 5, 10, 25]}
        pages={totalPages}
        sortable={false}
        showPagination={showPagination}
        style={{
          height: '490px',
        }}
      />
    );
  };

  const renderFooter = () => {
    return <div className="footer" />;
  };

  useEffect(() => {
    getRedemptions({ page, pageSize });
  }, []);

  if (error) {
    return <FileNotFound className="error">{error}</FileNotFound>;
  }

  return (
    <div className="redemption-list loyalty-list">
      <div className="current-search" onClick={onQueryClick}>
        <span className="current-label">Query</span>
        {query.length === 0 ? (
          <div className="search-data">
            {customerId ? `Showing All Redemptions for Customer ${customerId}` : 'No Search Criteria'}
          </div>
        ) : (
          <>
            {query.map(q => {
              return (
                <div className="search-data" key={q.label}>
                  {q.value === true ? (
                    <>
                      <strong>{q.label}</strong>
                    </>
                  ) : (
                    <>
                      {q.label}: <strong>{q.value}</strong>
                    </>
                  )}
                </div>
              );
            })}
          </>
        )}
        {totalPages !== -1 && (
          <div className="total">
            <strong>{total}</strong>
          </div>
        )}
      </div>
      {renderTable()}
      {renderFooter()}
    </div>
  );
};

RedemptionList.propTypes = {
  customerId: PropTypes.number,
  params: PropTypes.object,
  query: PropTypes.array,
  onQueryClick: PropTypes.func,
};

export default RedemptionList;
